import { Injectable, inject } from "@angular/core";
import { Observable, forkJoin, from } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { BlobDownloadResponseParsed, BlockBlobClient } from "@azure/storage-blob";

import { DocumentsService } from "./data/documents.service";
import { DocumentUploadItem } from "../types/documentuploaditem";
import { DocumentUploadModel } from "../types/documentuploadmodel";

@Injectable({
    "providedIn": "root"
})
export class AzureBlobService {
    private documentsService = inject(DocumentsService);

    uploadBlob(items: DocumentUploadItem[]): Observable<void> {
        const uploadInfos = items.map(item => item.uploadInfo);

        return this.documentsService.uploadDocument(uploadInfos).pipe(
            switchMap((uploadModels: DocumentUploadModel[]) => 
                this.uploadFilesToBlob(items, uploadModels)
            ),
            switchMap((uploadedModels: DocumentUploadModel[]) => {              
                const blobStagingIds = uploadedModels.map(model => model.blobStagingId);
                return this.documentsService.uploadComplete(blobStagingIds)
            })
        );
    }

    getBlob(sasUri: string): Observable<Blob> {
        const blobClient = new BlockBlobClient(sasUri);

        const download$ = from(blobClient.download(0));
        return download$.pipe(
            switchMap((downloadResponse: BlobDownloadResponseParsed) => {
                return from(downloadResponse.blobBody);
            })
        );
    }

    private matchFileToModel(item: DocumentUploadItem, uploadModels: DocumentUploadModel[]): DocumentUploadModel {
        const matchingModel = uploadModels.find(
            model => model.fileName === item.file.name
        );

        if (!matchingModel) {
            throw new Error(`No matching upload model found for file: ${item.file.name}`);
        }

        return matchingModel;
    }

    private uploadFileToBlob(item: DocumentUploadItem, uploadModel: DocumentUploadModel): Observable<DocumentUploadModel> {
        const blockBlobClient = new BlockBlobClient(uploadModel.documentUploadUrl);

        return from(
            item.file.arrayBuffer().then(buffer => 
                blockBlobClient.uploadData(buffer, {
                    blobHTTPHeaders: {
                        blobContentType: item.file.type
                    }
                })
            ).then(() => uploadModel)
        );
    }

    private uploadFilesToBlob(items: DocumentUploadItem[], uploadModels: DocumentUploadModel[]): Observable<DocumentUploadModel[]> {
        const uploadObservables = items.map(item => {
            const matchingModel = this.matchFileToModel(item, uploadModels);
            return this.uploadFileToBlob(item, matchingModel);
        });

        return forkJoin(uploadObservables);
    }
}

